import {
    ApiSet,
    BaseResponse,
    IndexApiSet,
    useDeleteApi,
    useDownloadApi,
    useIndexApi,
    usePatchApi,
    usePostApi,
    usePutApi,
    useShowApi,
} from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { RequestFormIssuance, RequestFormIssuanceForm, RequestFormIssuanceSearchForm } from 'entities/request_form_issuance'
import { YamatoDownloadForm } from './task'

type RequestFormIssuancesResponse = PagingResponse & {
    results: RequestFormIssuance[]
}

export function useFetchRequestFormIssuancesApi(
    searchForm: Form<RequestFormIssuanceSearchForm>
): IndexApiSet<RequestFormIssuancesResponse> & { execute: () => void } {
    const apiPath = 'request_form_issuances/'
    const api = useIndexApi<RequestFormIssuancesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm.object.page || 1, perPage: searchForm.object.perPage || 20 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm.object])

    return { ...api, execute: execute }
}

type SampleResponse = BaseResponse & RequestFormIssuance

export function useFetchRequestFormIssuanceApi(): ApiSet<SampleResponse> & { execute: (id: number) => void } {
    const api = useShowApi<SampleResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `request_form_issuances/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useDownloadCsvRequestFormIssuancesApi(searchForm: Form<RequestFormIssuanceSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'request_form_issuances/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'requestFormIssuance', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostRequestFormIssuanceApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormIssuanceForm>) => void } {
    const apiSet = usePostApi<BaseResponse, RequestFormIssuanceForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<RequestFormIssuanceForm>) => {
        const apiPath = `request_form_issuances/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutRequestFormIssuanceApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormIssuanceForm>) => void } {
    const apiSet = usePutApi<BaseResponse, RequestFormIssuanceForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<RequestFormIssuanceForm>) => {
        const apiPath = `request_form_issuances/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutRequestFormIssuancesApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormIssuanceForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, RequestFormIssuanceForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<RequestFormIssuanceForm[]>) => {
        const apiPath = `request_form_issuances/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteRequestFormIssuanceApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `request_form_issuances/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export type RequestFormIssuancePrintForm = {
    ids?: string
}

export function usePrintRequestFormIssuanceApi(): ApiSet<BaseResponse> & { execute: (id: number, form: Form<RequestFormIssuancePrintForm>) => void } {
    const apiSet = usePatchApi<BaseResponse, RequestFormIssuancePrintForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number, form: Form<RequestFormIssuancePrintForm>) => {
        const apiPath = `request_form_issuances/${id}/print/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDownloadYamatoForHomeTestApi(): ApiSet<BaseResponse> & { execute: (requestFormIssuanceId: number, form: Form<YamatoDownloadForm>) => void } {
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (requestFormIssuanceId: number, form: Form<YamatoDownloadForm>): void => {
        const apiPath = `request_form_exports/${requestFormIssuanceId}/export/`
        api.execute(apiPath, `newb2web_template1.csv`, form.object)
    }

    return { ...api, execute: execute }
}
